import React, { useState, useEffect } from 'react';
import { Check, Star, Zap, Shield, Crown, ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import { useAuth, AuthModal, UserMenu } from '../auth/AuthComponents';
import { apiRequest } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import ContactSupportForm from '../shared/ContactSupportForm';
import { publicPlanLabel } from '../../utils/planLabels';
import DocxsAiLogo from '../shared/DocxsAiLogo';

const planRewardLine = (plan) => {
  if (plan?.name === 'enterprise') {
    return 'Reward: a custom monthly document quota, plus priority help and full platform access.';
  }
  if (plan?.name === 'free') {
    return 'Reward: 5 documents per day to try analyze, revamp, and generate.';
  }
  const n = plan?.monthly_contracts || plan?.limit || 0;
  return `Reward: ${n} documents access per month (analyze, revamp & generate), plus job alerts, SOP opportunities, and more`;
};

const planCta = (plan) => {
  if (plan?.name === 'free') return 'Start Free';
  if (plan?.name === 'enterprise') return 'Contact Us';
  return `Get ${publicPlanLabel(plan)}`;
};

const MobilePricingPage = ({ setShowAuthModal: setShowAuthModalProp }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [showAuthModalLocal, setShowAuthModalLocal] = useState(false);
  const setShowAuthModal = setShowAuthModalProp || setShowAuthModalLocal;
  const showAuthModal = setShowAuthModalProp ? undefined : showAuthModalLocal;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiRequest('subscription/plans');
        const mappedPlans = (data.plans || []).map(plan => {
          let color = 'gray', icon = <Zap />, popular = false;
          if (plan.name === 'starter') {
            color = 'blue'; icon = <Star />; popular = true;
          } else if (plan.name === 'professional') {
            color = 'purple'; icon = <Shield />; popular = false;
          } else if (plan.name === 'enterprise') {
            color = 'gold'; icon = <Crown />;
          }
          const enriched = { ...plan, color, icon, popular };
          return { ...enriched, cta: planCta(enriched) };
        });
        setPlans(mappedPlans);
      } catch (err) {
        setError('Failed to load plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const navLinks = [
    { name: 'Home', action: () => navigate('/home'), current: false },
    { name: 'About', action: () => navigate('/about'), current: false },
    { name: 'Pricing', href: '#', current: true },
  ];

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `₦${(price / 100).toLocaleString()}`;
  };

  const getColorClasses = (color, popular = false) => {
    const colors = {
      gray: {
        border: 'border-[var(--color-border)]',
        bg: 'elevated-card',
        icon: 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]',
        button: 'brand-gradient-btn',
        accent: 'text-[var(--color-text-muted)]'
      },
      blue: {
        border: popular ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-ring)]' : 'border-[var(--color-primary)]/40',
        bg: popular ? 'elevated-card bg-gradient-to-br from-[var(--color-primary-soft)] to-[var(--color-surface)]' : 'elevated-card',
        icon: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
        button: 'brand-gradient-btn',
        accent: 'text-[var(--color-primary)]'
      },
      purple: {
        border: popular ? 'border-[var(--color-primary)] ring-2 ring-[var(--color-ring)]' : 'border-[var(--color-primary)]/40',
        bg: popular ? 'elevated-card bg-gradient-to-br from-[var(--color-primary-soft)] to-[var(--color-surface)]' : 'elevated-card',
        icon: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]',
        button: 'brand-gradient-btn',
        accent: 'text-[var(--color-primary)]'
      },
      gold: {
        border: 'border-yellow-500/40',
        bg: 'elevated-card bg-yellow-500/10',
        icon: 'bg-yellow-500/20 text-yellow-600',
        button: 'bg-yellow-600 hover:bg-yellow-700 text-[var(--color-text-inverse)] font-semibold',
        accent: 'text-yellow-600'
      }
    };
    return colors[color];
  };

  const handleSelectPlan = (plan) => {
    if (plan.name === 'enterprise') {
      setShowContactModal(true);
      return;
    }
    if (isAuthenticated) {
      navigate('/subscriptions');
    } else {
      setShowAuthModal(true);
    }
  };

  const totalSlides = plans.length;
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center text-red-400">{error}</div>
      </div>
    );
  }
  if (!plans.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center text-[var(--color-text-secondary)]">No plans available at this time.</div>
      </div>
    );
  }

  const plan = plans[currentSlide];
  const isEnterprise = plan.name === 'enterprise';

  return (
    <>
      <div className="min-h-screen public-page-glow relative overflow-hidden flex flex-col">
        {/* Mobile Navigation Header */}
        <nav className="bg-[var(--color-surface)] backdrop-blur-md border-b border-[var(--color-border)] sticky top-0 z-30">
          <div className="px-4 py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <DocxsAiLogo className="h-8 w-auto" />
              </div>
              {/* Mobile Hamburger Menu - Always show hamburger on mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] transition-colors"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)]">
              <div className="px-4 pt-3 pb-3 space-y-3">
                {navLinks.map((link) => (
                  link.action ? (
                    <button
                      key={link.name}
                      onClick={() => {
                        link.action();
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                        link.current 
                          ? 'text-[var(--color-text)] bg-[var(--color-surface-muted)]' 
                          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a
                      key={link.name}
                      href={link.href}
                      className={`block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                        link.current 
                          ? 'text-[var(--color-text)] bg-[var(--color-surface-muted)]' 
                          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {link.name}
                    </a>
                  )
                ))}
              </div>
              <div className="pt-4 pb-6 border-t border-[var(--color-border)] px-4">
                {isAuthenticated ? (
                  <button
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="brand-gradient-btn w-full px-4 py-3 rounded-xl"
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                    className="brand-gradient-btn w-full px-4 py-3 rounded-xl"
                  >
                    Get Started
                  </button>
                )}
              </div>
            </div>
          )}
        </nav>

        {/* Header */}
        <div className="text-center mt-6 mb-4 px-4">
          <h1 className="text-3xl font-bold text-[var(--color-text)] mb-2 leading-tight">
            Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">Perfect Plan</span>
          </h1>
          <p className="text-base text-[var(--color-text-muted)] mb-4">
            Unlock AI-powered document analysis, revamp, and generation. Pick a plan with a clear monthly document allowance that fits how you work.
          </p>
          {/* Billing Toggle */}
          <div className="flex items-center justify-center mb-4 pt-2">
            <div className="bg-[var(--color-surface)] public-input-elevated rounded-2xl p-1 flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                  billingCycle === 'monthly'
                    ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`relative px-4 py-2 rounded-xl font-medium transition-all ${
                  billingCycle === 'yearly'
                    ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 text-[9px] font-semibold text-white bg-[var(--color-success)] px-1.5 py-0.5 rounded-full whitespace-nowrap leading-none shadow-sm">
                  -20%
                </span>
                Yearly
              </button>
            </div>
          </div>
        </div>

        {/* Plan Carousel */}
        <div className="relative px-4">
          <div className="overflow-hidden pt-3">
            <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
              {plans.map((plan, slideIndex) => {
                const colors = getColorClasses(plan.color, plan.popular);
                const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
                const yearlyDiscount = billingCycle === 'yearly' && plan.yearly_price > 0 && plan.monthly_price > 0 && plan.name !== 'enterprise';
                const isEnterprise = plan.name === 'enterprise';
                return (
                  <div key={slideIndex} className="w-full flex-shrink-0 px-1">
                    <div className="public-tilt-wrap">
                    <div className={`relative backdrop-blur-sm border rounded-2xl p-5 transition-all duration-300 ${colors.border} ${colors.bg}`}>
                      {plan.popular && (
                        <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 z-10">
                          <div className="bg-[var(--color-surface-muted)]/95 border border-[var(--color-primary)]/40 text-[var(--color-primary)] px-2.5 py-0.5 rounded-full text-[10px] font-medium tracking-wide whitespace-nowrap">
                            Most Popular
                          </div>
                        </div>
                      )}
                      <div className="flex items-center space-x-4 mb-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colors.icon}`}>
                          {React.cloneElement(plan.icon, { className: "w-6 h-6" })}
                        </div>
                        <h3 className="text-xl font-bold text-[var(--color-text)]">{publicPlanLabel(plan)}</h3>
                      </div>
                      <div className="text-3xl font-bold text-[var(--color-text)] mb-2">
                        {isEnterprise ? 'Custom' : formatPrice(price)}
                        {!isEnterprise && <span className="text-base font-medium text-[var(--color-text-muted)]">/month</span>}
                        {yearlyDiscount && (
                          <div className="text-sm font-medium text-[var(--color-success)] mt-1">
                            Save ₦{(((plan.monthly_price || 0) * 12 - (plan.yearly_price || 0)) / 100).toLocaleString()} per year
                          </div>
                        )}
                      </div>
                      <div className="text-left bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-xl px-3 py-3 mb-4">
                        <p className="text-xs uppercase tracking-wide text-[var(--color-primary)] font-semibold mb-1">
                          What you unlock
                        </p>
                        <p className="text-[var(--color-text-secondary)] text-sm leading-relaxed">
                          {planRewardLine(plan)}
                        </p>
                      </div>
                      {/* Features */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(plan.features).filter(f => plan.features[f]).map((feature, featureIndex) => (
                            <div key={featureIndex} className="flex items-center space-x-2 bg-[var(--color-surface-muted)] rounded-lg px-3 py-2 flex-shrink-0">
                              <Check className="w-4 h-4 text-[var(--color-success)] flex-shrink-0" />
                              <span className="text-[var(--color-text-secondary)] text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* CTA Button */}
                      <div className="text-center">
                        <button
                          onClick={() => {
                            if (isEnterprise) {
                              setShowContactModal(true);
                            } else {
                              handleSelectPlan(plan);
                            }
                          }}
                          className={`w-full px-6 py-3 rounded-xl transition-colors text-lg ${
                            isEnterprise 
                              ? "bg-yellow-600 hover:bg-yellow-700 text-[var(--color-text-inverse)] font-semibold" 
                              : "brand-gradient-btn"
                          }`}
                        >
                          {isEnterprise ? 'Contact Us' : plan.cta}
                        </button>
                      </div>
                    </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Carousel Controls */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 p-3 bg-[var(--color-surface-muted)]/80 hover:bg-gray-700/80 rounded-full transition-all z-10"
          >
            <ChevronLeft className="w-6 h-6 text-[var(--color-text)]" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 p-3 bg-[var(--color-surface-muted)]/80 hover:bg-gray-700/80 rounded-full transition-all z-10"
          >
            <ChevronRight className="w-6 h-6 text-[var(--color-text)]" />
          </button>
          {/* Slide Indicators */}
          <div className="flex justify-center space-x-3 mt-4">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentSlide === index 
                    ? 'bg-[var(--color-primary)]' 
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
          {showContactModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-[var(--color-bg)] rounded-xl shadow-2xl p-6 max-w-lg w-full relative">
                <button
                  className="absolute top-3 right-3 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  onClick={() => setShowContactModal(false)}
                >
                  <X className="w-6 h-6" />
                </button>
                <h2 className="text-xl font-bold text-[var(--color-text)] mb-4">Contact Us About Enterprise Plan</h2>
                <ContactSupportForm
                  initialName={user?.full_name || ''}
                  initialEmail={user?.email || ''}
                  onClose={() => setShowContactModal(false)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
      </div>
      <AuthModal 
        isOpen={setShowAuthModalProp ? false : showAuthModalLocal} 
        onClose={() => setShowAuthModalLocal(false)} 
      />
    </>
  );
};

export default MobilePricingPage; 